/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: Moment library for date and time formatting.
import _ from "underscore"; // Npm: utility module.
import { useQuery } from "@apollo/client"; // Npm: Apollo client for GraphQL queries.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI DataGrid component.
import {
  Button,
  Flex,
  Box,
  Checkbox,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import MccUpsert from "components/MccUpsert";
import MccDropDownOptions from "components/MccDropDownOptions";
import MccDelete from "components/MccDelete";
import { MemoizedInput } from "components/MemoizedInput";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * GRAPHS
 */
import MccReadQuery from "./__query__/index.mcc.read.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });

  // Hook assignment .
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });

  // Hook assignment - Search states.
  const [mccToSearch, setMccToSearch] = React.useState("");
  const [dialingCodeToSearch, setDialingCodeToSearch] = React.useState("");
  const [countryNameToSearch, setCountryNameToSearch] = React.useState("");

  // Hook assignment .
  const {
    isOpen: isMccCreateOpen,
    onOpen: onMccCreateOpen,
    onClose: onMccCreateClose,
  } = useDisclosure();
  const {
    isOpen: isMccUpdateOpen,
    onOpen: onMccUpdateOpen,
    onClose: onMccUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isMccDeleteOpen,
    onOpen: onMccDeleteOpen,
    onClose: onMccDeleteClose,
  } = useDisclosure();

  const _mccReadVariables = React.useMemo(
    () => ({
      mccToSearch,
      dialingCodeToSearch,
      countryNameToSearch,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    }),
    [
      countryNameToSearch,
      dialingCodeToSearch,
      mccToSearch,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);

  // Previous pagination uses Apollo cache-first so visited pages do not hit backend again.
  const _QueryMccRead = useQuery(MccReadQuery, {
    variables: _mccReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });

  const { refetch } = _QueryMccRead;
  // Ref assignment - Track first load completion and table header height.
  const _isFirstLoadCompleted = React.useRef(false);
  const _rowCountRef = React.useRef(0);
  const _tableHeaderHeightRef = React.useRef(0);

  // Responsive view detection.
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Debounce handlers .
  const _onMccInputChange = React.useRef(
    _.debounce((e) => setMccToSearch(e.target.value), 800),
  ).current;
  const _onDialingCodeInputChange = React.useRef(
    _.debounce((e) => setDialingCodeToSearch(e.target.value), 800),
  ).current;
  const _onCountryNameInputChange = React.useRef(
    _.debounce((e) => setCountryNameToSearch(e.target.value), 800),
  ).current;

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onMccInputChange.cancel?.();
      _onDialingCodeInputChange.cancel?.();
      _onCountryNameInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryMccRead.data) {
      // Reset states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [isMccCreateOpen, isMccUpdateOpen, isMccDeleteOpen, _QueryMccRead.data]);

  // Object assignment.
  const _HandleCheckboxChange = (id) => {
    // Update state.
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const _HandleSelectAll = () => {
    // Update state.
    setSelectAll(!selectAll);
    setSelectedIds(
      selectAll
        ? []
        : _QueryMccRead?.data?.MccRead?.map((item) => item.id) || [],
    );
  };

  // Data assignment - Check if first load is completed.
  if (!_QueryMccRead.loading && 0 < _QueryMccRead.data?.MccRead?.length) {
    _isFirstLoadCompleted.current = true;
  }

  if (
    0 === _QueryMccRead.data?.MccRead?.length ||
    (0 < _QueryMccRead.data?.MccRead?.length &&
      !_.every(_.pluck(_QueryMccRead.data?.MccRead, "status"), (i) =>
        _successFlags?.includes(i),
      ))
  ) {
    _isFirstLoadCompleted.current = false;
  }

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryMccRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryMccRead.refetch]);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);

  // Columns definition .
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIds.includes(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: false,
      },
      {
        field: "dialingCode",
        headerName: "Dialing Code",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "mcc",
        headerName: "Mcc",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "countryName",
        headerName: "Country",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "minOperatorPrefixLength",
        headerName: "MinOperatorPrefixLength",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "maxOperatorPrefixLength",
        headerName: "MaxOperatorPrefixLength",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "action",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <MccDropDownOptions
            mccIds={_.union(selectedIds, [row.id])}
            mccId={row.id}
            onMccUpdate={onMccUpdateOpen}
            onMccDelete={onMccDeleteOpen}
            selectedIds={selectedIds}
          />
        ),
      },
    ],
    [selectAll, selectedIds],
  );

  // Rows mapping .
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryMccRead.data?.MccRead?.map((item, index) =>
          _successFlags.includes(item?.status)
            ? {
                id: item?.id ?? `mcc-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                dialingCode: item?.dialingCode ?? "-",
                mcc: item?.mcc ?? "-",
                countryName: item?.countryName ?? "-",
                minOperatorPrefixLength: item?.minOperatorPrefixLength ?? "-",
                maxOperatorPrefixLength: item?.maxOperatorPrefixLength ?? "-",
                createdAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                updatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
              }
            : null,
        ),
      ) || [],
    [
      _QueryMccRead.data?.MccRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _rowCount = _.first(_QueryMccRead?.data?.MccRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount)
      _rowCountRef.current = _rowCount;

    return _rowCountRef.current;
  }, [_rowCount]);

  // Return component.
  return (
    <>
      <Flex className="mcc base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          minH={_isCurrentViewMobile ? "130vh" : "99vh"}
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section */}
          <Flex
            pb="0px"
            justify="space-between"
            overflow="hidden"
            flexWrap={{ base: "wrap", md: "wrap" }}
          >
            <Flex gap="12px">
              <Tooltip label="Create New MCC" fontSize="sm">
                <Button
                  onClick={onMccCreateOpen}
                  leftIcon={<IoMdAdd />}
                  style={buttonStyle}
                >
                  Create Mcc
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={
                  0 === _QueryMccRead.data?.MccRead?.length ||
                  !_.every(
                    _.pluck(_QueryMccRead.data?.MccRead, "status"),
                    (i) => "READ_SUCCESSFUL" === i,
                  )
                    ? []
                    : _QueryMccRead.data?.MccRead?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "Dialing Code": item?.dialingCode ?? "-",
                        Mcc: item?.mcc ?? "-",
                        Country: item?.countryName ?? "-",
                        MinOperatorPrefixLength:
                          item?.minOperatorPrefixLength ?? "-",
                        MaxOperatorPrefixLength:
                          item?.maxOperatorPrefixLength ?? "-",
                        CreatedAt: Moment(item.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item.updatedAt).format("YYYY-MM-DD"),
                      }))
                }
                headersData={[
                  "S.No.",
                  "Dialing Code",
                  "Mcc",
                  "Country",
                  "MinOperatorPrefixLength",
                  "MaxOperatorPrefixLength",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section*/}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Dialing Code
              </Text>
              <MemoizedInput
                placeholder="Search by Dialing Code"
                className="filter searchInput"
                defaultValue={dialingCodeToSearch}
                onChange={_onDialingCodeInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccToSearch}
                onChange={_onMccInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameInputChange}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid  */}
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryMccRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="Mcc Create"
          isOpen={isMccCreateOpen}
          onClose={onMccCreateClose}
        >
          <MccUpsert
            isOpen={isMccCreateOpen}
            isCreateOnly={true}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Mcc Update"
          isOpen={isMccUpdateOpen}
          onClose={onMccUpdateClose}
        >
          <MccUpsert isOpen={isMccUpdateOpen} refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Mcc Delete"
          isOpen={isMccDeleteOpen}
          onClose={onMccDeleteClose}
        >
          <MccDelete refetch={refetch} />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default Index;
